import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Search, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useParents, useUpdateMapping, type PortfolioParent } from '@/api/portfolios'

interface ReParentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  childAsin: string | null
  currentParentId?: number | null
}

export default function ReParentDialog({ open, onOpenChange, childAsin, currentParentId }: ReParentDialogProps) {
  const { data: parents, isLoading } = useParents()
  const update = useUpdateMapping()

  const [query, setQuery] = useState('')
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Reset beim Öffnen
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedParentId(null)
      setReason('')
      setSubmitError(null)
    }
  }, [open, childAsin])

  // ESC zum Schließen
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onOpenChange])

  const filtered = useMemo(() => {
    const list = parents ?? []
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter(
      (p) =>
        p.amazonParentAsin.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.ppcCode ?? '').toLowerCase().includes(q),
    )
  }, [parents, query])

  if (!open) return null

  async function handleSubmit() {
    if (!childAsin || selectedParentId == null) {
      setSubmitError('Bitte neuen Parent auswählen')
      return
    }
    setSubmitError(null)
    try {
      await update.mutateAsync({
        childAsin,
        newParentId: selectedParentId,
        reason: reason.trim() || undefined,
      })
      onOpenChange(false)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unbekannter Fehler')
    }
  }

  const selected = parents?.find((p) => p.id === selectedParentId) ?? null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">Re-Parent Zuordnung</h2>
            <p className="text-xs text-[var(--muted-foreground)]">
              Child-ASIN: <span className="font-mono">{childAsin ?? '—'}</span>
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-4 py-4">
          {/* Parent-Suche */}
          <div>
            <label className="mb-1 block text-xs font-medium">Neuer Parent</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <input
                type="text"
                placeholder="ASIN, Name oder PPC-Code suchen..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
              />
            </div>
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-[var(--border)]">
              {isLoading ? (
                <div className="py-4 text-center text-xs text-[var(--muted-foreground)]">Laden...</div>
              ) : filtered.length === 0 ? (
                <div className="py-4 text-center text-xs text-[var(--muted-foreground)]">
                  Keine Treffer
                </div>
              ) : (
                filtered.map((p) => (
                  <ParentRow
                    key={p.id}
                    parent={p}
                    selected={p.id === selectedParentId}
                    disabled={p.id === currentParentId}
                    onClick={() => setSelectedParentId(p.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Grund */}
          <div>
            <label className="mb-1 block text-xs font-medium">
              Grund (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="z.B. Falsche Parent-Zuordnung durch Amazon-Auto-Match"
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
            />
          </div>

          {/* Override-Checkbox */}
          <label className="flex items-start gap-2 rounded-md border border-[var(--border)] bg-[var(--muted)] px-3 py-2">
            <input type="checkbox" checked disabled className="mt-0.5 rounded" />
            <span className="text-xs">
              Als <strong>manueller Override</strong> markieren. Der Auto-Matcher
              überschreibt diese Zuordnung künftig nicht mehr (is_override=true).
            </span>
          </label>

          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] px-4 py-3">
          <div className="text-xs text-[var(--muted-foreground)] truncate">
            {selected ? (
              <>
                Ziel:{' '}
                <span className="font-mono">{selected.amazonParentAsin}</span>
                {selected.ppcCode && <> · {selected.ppcCode}</>}
              </>
            ) : (
              'Kein Parent ausgewählt'
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedParentId == null || update.isPending}
              className={cn(
                'rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-40',
              )}
            >
              {update.isPending ? 'Speichere...' : 'Zuordnung speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ParentRow({
  parent,
  selected,
  disabled,
  onClick,
}: {
  parent: PortfolioParent
  selected: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center justify-between border-b border-[var(--border)] px-3 py-2 text-left text-sm last:border-b-0 hover:bg-[var(--muted)]',
        selected && 'bg-[var(--accent)]',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{parent.amazonParentAsin}</span>
          {parent.ppcCode && (
            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-700">
              {parent.ppcCode}
            </span>
          )}
          {disabled && (
            <span className="text-[10px] text-[var(--muted-foreground)]">(aktueller Parent)</span>
          )}
        </div>
        <div className="truncate text-xs text-[var(--muted-foreground)]">{parent.name}</div>
      </div>
      {selected && <Check className="ml-2 h-4 w-4 text-emerald-600 shrink-0" />}
    </button>
  )
}
